import { useCallback, useEffect, useState, type DragEvent } from "react";
import axios from "axios";
import { PrismBanner, PrismButton, PrismSelect } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { apiError, relativeTime } from "./RatingWindowTab";
import { APPLICATION_STATUS_ID_ACCEPTED } from "../../../Application/constants";
import useConfirm from "../../../../hooks/useConfirm";

export interface WaitlistEntry {
  application_id: number;
  applicant_type_id: number;
  status_id: number;
  waitlist_position: number | null;
  name: string | null;
  email: string | null;
  average?: number | null;
  rating_count?: number;
}

type TrackKey = "mentee" | "mentor" | "committee";

interface TrackSeats {
  available: number | null;
  waitlist: number;
}

interface WaitlistResponse {
  year: number;
  seats: Record<TrackKey, TrackSeats> | null;
  review_open?: boolean;
  entries: WaitlistEntry[];
}

export interface ActivityItem {
  kind: "status" | "reorder" | "seats";
  at: string;
  by: string | null;
  track: TrackKey;
  name?: string | null;
  from?: number | null;
  to?: number | null;
  field?: "available" | "waitlist";
  count?: number;
}

interface SeatMove {
  application_id: number;
  name: string;
  average: number | null;
  waitlist_position: number;
}

interface SeatPlan {
  moved: SeatMove[];
  backups: Record<string, number>;
  backup_raised: TrackKey[];
}

const STATUS_REJECTED = 5;
const STATUS_WAITLISTED = 8;
const STATUS_DECLINED = 9;
const BACKUP_FIELD: Record<TrackKey, string> = {
  mentee: "mentee_backup_seats",
  mentor: "mentor_backup_seats",
  committee: "tcm_backup_seats",
};

const TRACKS: { key: TrackKey; typeId: number; label: string; singular: string }[] = [
  { key: "mentee", typeId: 1, label: "Mentees", singular: "mentee" },
  { key: "mentor", typeId: 2, label: "Mentors", singular: "mentor" },
  { key: "committee", typeId: 3, label: "Committee Members", singular: "committee member" },
];

const displayName = (e: WaitlistEntry) => e.name ?? "Unknown applicant";

const ratingText = (e: WaitlistEntry) =>
  e.average === null || e.average === undefined
    ? "Unrated"
    : `Avg ${e.average.toFixed(2)} · ${e.rating_count ?? 0} rating${e.rating_count === 1 ? "" : "s"}`;

// `opensSeat` is false when the track holds more accepted than seats, so a decline frees nothing.
export const declineMessage = (entry: WaitlistEntry, nextUp: WaitlistEntry | undefined, opensSeat = true) =>
  !opensSeat
    ? `Record that ${displayName(entry)} declined their seat? The track is still over its seats, so nobody is promoted.`
    : nextUp
      ? `Record that ${displayName(entry)} declined their seat? ${displayName(nextUp)} (waitlist #${nextUp.waitlist_position}) will be promoted to Accepted.`
      : `Record that ${displayName(entry)} declined their seat? Nobody is waitlisted, so the seat will stay open.`;

const seatText = (n: number | null | undefined) => (n === null || n === undefined ? "no limit" : String(n));

// One line of the activity feed, without who or when.
export const activityText = (item: ActivityItem): string => {
  if (item.kind === "reorder") return "Waitlist reordered";
  if (item.kind === "seats") {
    return item.field === "waitlist"
      ? `Waitlist seats changed from ${item.from ?? 0} to ${item.to ?? 0}`
      : `Seats changed from ${seatText(item.from)} to ${seatText(item.to)}`;
  }
  const name = item.name ?? "An applicant";
  const { from, to } = item;
  if (from === STATUS_WAITLISTED && to === APPLICATION_STATUS_ID_ACCEPTED) return `${name} promoted from the waitlist`;
  if (from === APPLICATION_STATUS_ID_ACCEPTED && to === STATUS_DECLINED) return `${name} declined their seat`;
  if (from === STATUS_DECLINED && to === APPLICATION_STATUS_ID_ACCEPTED) return `${name}'s decline was undone`;
  if (from === STATUS_REJECTED && to === STATUS_WAITLISTED) return `${name} added to the waitlist`;
  if (from === STATUS_WAITLISTED && to === STATUS_REJECTED) return `${name} removed from the waitlist`;
  if (from === APPLICATION_STATUS_ID_ACCEPTED && to === STATUS_WAITLISTED) return `${name} moved to the waitlist`;
  if (to === APPLICATION_STATUS_ID_ACCEPTED) return `${name} accepted`;
  if (to === STATUS_WAITLISTED) return `${name} waitlisted`;
  if (to === STATUS_DECLINED) return `${name} declined`;
  return `${name}'s status changed`;
};

const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const isConflict = (err: unknown) => (err as { response?: { status?: number } })?.response?.status === 409;

const WaitlistTab = ({ chapterId }: { chapterId: number }) => {
  const [data, setData] = useState<WaitlistResponse | null>(null);
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [track, setTrack] = useState<TrackKey>("mentee");
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ available: string; waitlist: string } | null>(null);
  const [adding, setAdding] = useState("");
  const [dragId, setDragId] = useState<number | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  const base = `/api/rater_admin/waitlist/${chapterId}`;

  const load = useCallback(async () => {
    try {
      const { data: body } = await axios.get(`/api/rater_admin/waitlist/${chapterId}`);
      setData(body);
    } catch (err) {
      toast.error(apiError(err, "Unable to load waitlist"));
    }
  }, [chapterId]);

  const loadActivity = useCallback(async () => {
    try {
      const { data: body } = await axios.get(`/api/rater_admin/waitlist/${chapterId}/activity`);
      setActivity(Array.isArray(body?.items) ? body.items : []);
    } catch {
      setActivity([]);
    }
  }, [chapterId]);

  const refresh = useCallback(() => Promise.all([load(), loadActivity()]), [load, loadActivity]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const chooseTrack = (key: TrackKey) => {
    setTrack(key);
    setEditing(null);
    setAdding("");
  };

  if (!data) return <p>Loading…</p>;

  const byTrack = Object.fromEntries(
    TRACKS.map((t) => {
      const mine = data.entries.filter((e) => e.applicant_type_id === t.typeId);
      return [
        t.key,
        {
          accepted: mine.filter((e) => e.status_id === APPLICATION_STATUS_ID_ACCEPTED),
          waitlisted: mine
            .filter((e) => e.status_id === STATUS_WAITLISTED)
            .sort((a, b) => (a.waitlist_position ?? 0) - (b.waitlist_position ?? 0)),
          declined: mine.filter((e) => e.status_id === STATUS_DECLINED),
          rejected: mine
            .filter((e) => e.status_id === STATUS_REJECTED)
            .sort((a, b) => (b.average ?? -Infinity) - (a.average ?? -Infinity)),
        },
      ];
    })
  ) as Record<
    TrackKey,
    { accepted: WaitlistEntry[]; waitlisted: WaitlistEntry[]; declined: WaitlistEntry[]; rejected: WaitlistEntry[] }
  >;

  const current = TRACKS.find((t) => t.key === track) ?? TRACKS[0];
  const { accepted, waitlisted, declined, rejected } = byTrack[track];
  const seats = data.seats?.[track];
  const open = seats?.available != null ? Math.max(seats.available - accepted.length, 0) : null;
  const hasOpenSeat = open === null || open > 0;
  const waitlistFull = !!seats && waitlisted.length >= seats.waitlist;
  const nextUp = waitlisted[0];
  const trackActivity = (activity ?? []).filter((a) => a.track === track);

  // Runs one change, then reloads. A 409 means the screen was stale, so reload either way.
  const run = async (key: string, fallback: string, request: () => Promise<{ data: { notice?: string | null } }>, done: string) => {
    setBusy(key);
    try {
      const { data: body } = await request();
      toast.success(body?.notice || done);
      await refresh();
      return true;
    } catch (err) {
      toast.error(apiError(err, fallback));
      if (isConflict(err)) await refresh();
      return false;
    } finally {
      setBusy(null);
    }
  };

  const decline = async (entry: WaitlistEntry) => {
    const opensSeat = seats?.available == null || accepted.length <= seats.available;
    const ok = await confirm({
      title: "Record a decline?",
      message: declineMessage(entry, nextUp, opensSeat),
      confirmLabel: "Record decline",
      tone: "danger",
    });
    if (!ok) return;
    await run(
      `decline-${entry.application_id}`,
      "Unable to process decline",
      () => axios.post(`${base}/decline`, { application_id: entry.application_id }),
      "Decline recorded and the next waitlisted applicant was promoted."
    );
  };

  const undoDecline = async (entry: WaitlistEntry) => {
    const ok = await confirm({
      title: "Undo this decline?",
      message: `${displayName(entry)} goes back to Accepted and takes an open ${current.singular} seat.`,
      confirmLabel: "Undo decline",
      tone: "primary",
    });
    if (!ok) return;
    await run(
      `undo-${entry.application_id}`,
      "Unable to undo the decline",
      () => axios.post(`${base}/undo_decline`, { application_id: entry.application_id }),
      `${displayName(entry)} is Accepted again.`
    );
  };

  const promote = async () => {
    if (!nextUp) return;
    const ok = await confirm({
      title: "Promote from the waitlist?",
      message: `${displayName(nextUp)} (waitlist #${nextUp.waitlist_position}) moves to Accepted and takes an open ${current.singular} seat. No notification is sent.`,
      confirmLabel: "Promote",
      tone: "primary",
    });
    if (!ok) return;
    await run(
      "promote",
      "Unable to promote",
      () => axios.post(`${base}/promote`, { track, year: data.year }),
      `${displayName(nextUp)} promoted to Accepted.`
    );
  };

  const remove = async (entry: WaitlistEntry) => {
    const ok = await confirm({
      title: "Remove from the waitlist?",
      message: `${displayName(entry)} goes back to Rejected and everyone behind them moves up. You can add them back later.`,
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    await run(
      `remove-${entry.application_id}`,
      "Unable to remove from the waitlist",
      () => axios.post(`${base}/remove`, { application_id: entry.application_id }),
      `${displayName(entry)} removed from the waitlist.`
    );
  };

  const add = async () => {
    const entry = rejected.find((e) => String(e.application_id) === adding);
    if (!entry) return;
    const added = await run(
      "add",
      "Unable to add to the waitlist",
      () => axios.post(`${base}/add`, { application_id: entry.application_id }),
      `${displayName(entry)} added to the end of the waitlist.`
    );
    if (added) setAdding("");
  };

  // The new order shows at once; the server confirms it, or the screen reloads.
  const reorder = async (from: number, to: number) => {
    if (from === to || to < 0 || to >= waitlisted.length) return;
    const order = moveItem(waitlisted, from, to);
    const position = new Map(order.map((e, i) => [e.application_id, i + 1]));
    setData({
      ...data,
      entries: data.entries.map((e) =>
        position.has(e.application_id) ? { ...e, waitlist_position: position.get(e.application_id) ?? null } : e
      ),
    });
    setBusy("reorder");
    try {
      await axios.post(`${base}/reorder`, { track, year: data.year, order: order.map((e) => e.application_id) });
      await loadActivity();
    } catch (err) {
      toast.error(apiError(err, "Unable to reorder the waitlist"));
      await load();
    } finally {
      setBusy(null);
    }
  };

  const onDrop = (event: DragEvent, index: number) => {
    event.preventDefault();
    const from = waitlisted.findIndex((e) => e.application_id === dragId);
    setDragId(null);
    if (from >= 0) reorder(from, index);
  };

  const saveSeats = async () => {
    if (!editing) return;
    const available = editing.available.trim() === "" ? null : Number(editing.available);
    const waitlist = editing.waitlist.trim() === "" ? 0 : Number(editing.waitlist);
    const valid = (v: number | null) => v === null || (Number.isInteger(v) && v >= 0);
    if (!valid(available) || !valid(waitlist)) {
      toast.error("Seat counts must be whole numbers, zero or more");
      return;
    }
    const body = { track, year: data.year, available, waitlist };
    setBusy("seats");
    try {
      const { data: plan } = await axios.patch<SeatPlan>(`${base}/seats`, { ...body, preview: true });
      const moved = plan.moved ?? [];
      const raised = (plan.backup_raised ?? []).includes(track);
      if (moved.length > 0 || raised) {
        const ok = await confirm({
          title: moved.length > 0 ? `Move ${moved.length} to the waitlist?` : "Raise waitlist seats?",
          message: (
            <div className="seat-plan">
              {moved.length > 0 && (
                <>
                  <p>That is fewer seats than are already accepted. The lowest-rated move to the front of the waitlist:</p>
                  <ol>
                    {moved.map((m) => (
                      <li key={m.application_id}>
                        Waitlist #{m.waitlist_position}: {m.name}{" "}
                        <span className="seat-plan-average">
                          ({m.average === null ? "unrated" : `avg ${m.average.toFixed(2)}`})
                        </span>
                      </li>
                    ))}
                  </ol>
                </>
              )}
              {raised && <p>Waitlist seats rise to {plan.backups?.[BACKUP_FIELD[track]]} so everyone waitlisted fits.</p>}
            </div>
          ),
          confirmLabel: moved.length > 0 ? "Move to waitlist" : "Raise waitlist seats",
          tone: "primary",
        });
        if (!ok) return;
      }
      await axios.patch(`${base}/seats`, body);
      toast.success(`${current.label} seats saved.`);
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(apiError(err, "Unable to update seats"));
      if (isConflict(err)) await refresh();
    } finally {
      setBusy(null);
    }
  };

  const addOptions = [
    { value: "", display: rejected.length ? "Choose a rejected applicant" : "No rejected applicants" },
    ...rejected.map((e) => ({
      value: String(e.application_id),
      display: `${displayName(e)} (${e.average === null || e.average === undefined ? "unrated" : `avg ${e.average.toFixed(2)}`})`,
    })),
  ];

  return (
    <div className="waitlist">
      <PrismBanner tone="info">
        The accepted cohort and its waitlist for each track, in promotion order. Set seats and waitlist seats per track,
        drag (or use the arrows) to reorder the waitlist, promote into open seats, add a rejected applicant, or take
        someone off. When an accepted applicant declines, use <strong>Record decline</strong>: the first person on that
        track&apos;s waitlist is promoted to the open seat. Every change is logged below. No notification is sent from
        here.
      </PrismBanner>
      {data.review_open && (
        <PrismBanner tone="caution">
          A formal Host Review is still open for {data.year}. Changes here apply right away and the review picks them up
          the next time it loads.
        </PrismBanner>
      )}
      <div className="waitlist-heading">
        <span className="host-review-badge primary">{data.year} cohort</span>
        <span className="waitlist-heading-note">Always the current year&apos;s cohort.</span>
      </div>

      <div className="waitlist-tracks" role="tablist" aria-label="Track">
        {TRACKS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === track}
            className={t.key === track ? "active" : ""}
            onClick={() => chooseTrack(t.key)}
          >
            {t.label}
            <span className="waitlist-track-count" aria-label={`${byTrack[t.key].waitlisted.length} waitlisted`}>
              {byTrack[t.key].waitlisted.length}
            </span>
          </button>
        ))}
      </div>

      <section className="waitlist-panel" role="tabpanel" aria-label={current.label}>
        {seats ? (
          <div className="waitlist-seat-panel">
            <dl className="waitlist-seats">
              <div>
                <dt>Seats</dt>
                <dd>{seats.available ?? "—"}</dd>
              </div>
              <div>
                <dt>Accepted</dt>
                <dd>{accepted.length}</dd>
              </div>
              <div className={open ? "waitlist-seat-open" : ""}>
                <dt>Open</dt>
                <dd>{open ?? "—"}</dd>
              </div>
              <div>
                <dt>Waitlist</dt>
                <dd>
                  {waitlisted.length} / {seats.waitlist}
                </dd>
              </div>
              <div>
                <dt>Declined</dt>
                <dd>{declined.length}</dd>
              </div>
            </dl>
            {editing ? (
              <div className="waitlist-seat-editor" role="group" aria-label={`${current.label} seats`}>
                <label>
                  Seats
                  <input
                    type="number"
                    min={0}
                    placeholder="No limit"
                    value={editing.available}
                    onChange={(e) => setEditing({ ...editing, available: e.target.value })}
                  />
                </label>
                <label>
                  Waitlist seats
                  <input
                    type="number"
                    min={0}
                    placeholder="0"
                    value={editing.waitlist}
                    onChange={(e) => setEditing({ ...editing, waitlist: e.target.value })}
                  />
                </label>
                <PrismButton size="sm" disabled={busy !== null} onPrismClick={saveSeats}>
                  Save seats
                </PrismButton>
                <PrismButton size="sm" tone="secondary" display="outline" onPrismClick={() => setEditing(null)}>
                  Cancel
                </PrismButton>
                <small className="waitlist-muted">
                  Blank seats means no limit. Fewer seats than accepted moves the lowest-rated to the waitlist; you see
                  who first.
                </small>
              </div>
            ) : (
              <div className="waitlist-seat-actions">
                <PrismButton
                  size="sm"
                  tone="secondary"
                  display="outline"
                  onPrismClick={() =>
                    setEditing({
                      available: seats.available === null ? "" : String(seats.available),
                      waitlist: String(seats.waitlist),
                    })
                  }
                >
                  Edit seats
                </PrismButton>
                {nextUp && hasOpenSeat && (
                  <PrismButton size="sm" disabled={busy !== null} onPrismClick={promote}>
                    {open === null ? "Promote next" : `Promote next (${open} open)`}
                  </PrismButton>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="waitlist-muted">
            Seat targets show up here once a formal Host Review has run for {data.year}. Until then seats can&apos;t be
            edited and the waitlist has no cap.
          </p>
        )}

        <h3>Waitlist</h3>
        {waitlisted.length === 0 ? (
          <p className="waitlist-empty">
            Nobody is waitlisted for {current.label.toLowerCase()}. People land here when a formal Host Review accepts
            more {current.label.toLowerCase()} than there are seats
            {seats ? `, up to ${seats.waitlist} waitlist seat${seats.waitlist === 1 ? "" : "s"}` : ""}, or when you add
            them below.
          </p>
        ) : (
          <ol className="waitlist-queue" aria-label={`${current.label} waitlist`}>
            {waitlisted.map((e, i) => (
              <li
                key={e.application_id}
                className={[i === 0 ? "next" : "", dragId === e.application_id ? "dragging" : ""].join(" ").trim()}
                draggable={busy === null}
                onDragStart={(event) => {
                  setDragId(e.application_id);
                  event.dataTransfer?.setData("text/plain", String(e.application_id));
                }}
                onDragEnd={() => setDragId(null)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => onDrop(event, i)}
              >
                <span className="waitlist-grip" aria-hidden="true">
                  ⋮⋮
                </span>
                <span className="waitlist-position">#{e.waitlist_position}</span>
                <span className="waitlist-person">
                  <strong>{displayName(e)}</strong>
                  {e.email && <small>{e.email}</small>}
                </span>
                <span className="waitlist-rating">{ratingText(e)}</span>
                {i === 0 && <span className="waitlist-next-tag">Next up</span>}
                <span className="waitlist-row-actions">
                  <button
                    type="button"
                    className="waitlist-move"
                    aria-label={`Move ${displayName(e)} up`}
                    disabled={i === 0 || busy !== null}
                    onClick={() => reorder(i, i - 1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="waitlist-move"
                    aria-label={`Move ${displayName(e)} down`}
                    disabled={i === waitlisted.length - 1 || busy !== null}
                    onClick={() => reorder(i, i + 1)}
                  >
                    ↓
                  </button>
                  <PrismButton
                    size="sm"
                    tone="secondary"
                    display="outline"
                    disabled={busy !== null}
                    onPrismClick={() => remove(e)}
                  >
                    Remove
                  </PrismButton>
                </span>
              </li>
            ))}
          </ol>
        )}

        <div className="waitlist-add">
          <PrismSelect
            label="Add to the waitlist"
            value={adding}
            options={addOptions}
            disabled={rejected.length === 0 || waitlistFull || busy !== null}
            onPrismChange={(e: CustomEvent<{ value: string }>) => setAdding(String(e.detail?.value ?? ""))}
          />
          <PrismButton size="sm" disabled={!adding || waitlistFull || busy !== null} onPrismClick={add}>
            Add to end
          </PrismButton>
          {waitlistFull && (
            <small className="waitlist-muted">The waitlist is full. Raise its waitlist seats to add someone.</small>
          )}
        </div>

        <h3>Accepted ({accepted.length})</h3>
        {accepted.length === 0 ? (
          <p className="waitlist-empty">No {current.label.toLowerCase()} have been accepted for {data.year} yet.</p>
        ) : (
          <table className="application-rater-table waitlist-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Rating</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {accepted.map((e) => (
                <tr key={e.application_id}>
                  <td>{displayName(e)}</td>
                  <td className="waitlist-muted">{e.email ?? "—"}</td>
                  <td className="waitlist-muted">{ratingText(e)}</td>
                  <td className="waitlist-action">
                    <PrismButton
                      size="sm"
                      tone="secondary"
                      display="outline"
                      disabled={busy !== null}
                      onPrismClick={() => decline(e)}
                    >
                      Record decline
                    </PrismButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {declined.length > 0 && (
          <>
            <h3>Declined ({declined.length})</h3>
            <ul className="waitlist-declined">
              {declined.map((e) => (
                <li key={e.application_id}>
                  <span>{displayName(e)}</span>
                  <PrismButton
                    size="sm"
                    tone="secondary"
                    display="outline"
                    disabled={!hasOpenSeat || busy !== null}
                    title={hasOpenSeat ? undefined : "Every seat is filled. Raise the seat count first."}
                    onPrismClick={() => undoDecline(e)}
                  >
                    Undo decline
                  </PrismButton>
                </li>
              ))}
            </ul>
          </>
        )}

        <h3>Recent activity</h3>
        {activity === null ? (
          <p className="waitlist-muted">Loading activity…</p>
        ) : trackActivity.length === 0 ? (
          <p className="waitlist-empty">No {current.singular} seat or waitlist changes recorded for {data.year} yet.</p>
        ) : (
          <ul className="waitlist-activity">
            {trackActivity.map((item, i) => (
              <li key={`${item.at}-${i}`}>
                <span>{activityText(item)}</span>
                <small className="waitlist-muted">
                  {item.by ?? "Someone"} ·{" "}
                  <time dateTime={item.at} title={new Date(item.at).toLocaleString()}>
                    {relativeTime(new Date(item.at))}
                  </time>
                </small>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.entries.length === 0 && (
        <PrismBanner tone="info">
          Nobody has been accepted for {data.year} yet. Run a formal Host Review to build the cohort.
        </PrismBanner>
      )}
      {confirmDialog}
    </div>
  );
};

export default WaitlistTab;
